import moment from "moment";
import { initialize, change } from "redux-form/immutable";

import { toggleModal } from "altus-modal";
import { NOTIFICATION_VARIANTS } from "altus-redux-middlewares";

import {
  getCard,
  getCardOpenDeployments,
  updateCardOpenDeployment,
  getCrews,
  getEmployeeGroups,
  getGroup,
  getMarkers,
  getQualifications,
  getReplaceableEmployees,
  getCardWorkDays,
  getCardAllDeployments,
  getGroupProjects,
} from "features/planner/planner.actions";

import {
  ACTIONS,
  UPDATE_EMPLOYEE_FORM,
  WORK_SCHEDULE_FORM,
  WORK_SCHEDULE_OVERRIDE_FORM,
  CREATE_WORK_SCHEDULE_OVERRIDE_MODAL_ID,
  EDIT_WORK_SCHEDULE_OVERRIDE_MODAL_ID,
  CREATE_WORK_SCHEDULE_MODAL_ID,
  EDIT_WORK_SCHEDULE_MODAL_ID,
  SIMPLE_EDIT_WORK_SCHEDULE_MODAL_ID,
} from "features/planner/employee/employee.constants";

import {
  getWorkScheduleDefinitionFromState,
  getWorkScheduleDefinitionsFromState,
} from "features/planner/employee/employee.selectors";
import {
  WORK_SCHEDULE_OVERRIDE_REASONS,
  CREATE_CARD_MOVE_FORM,
} from "features/planner/planner.constants";
import {
  getCardFromState,
  getDeploymentDetailByCardIdFromState,
} from "features/planner/planner.selectors";

import cardService from "services/card.service";
import { makeActionCreator, invokeIfFunction } from "utils/app.util";
import { workScheduleOverrideReasonToString } from "features/planner/planner.mappers";

export const getEmployeeWorkSchedules = makeActionCreator(
  {
    type: ACTIONS.GET_EMPLOYEE_WORK_SCHEDULES,
    payload: cardService.getEmployeeWorkSchedules,
  },
  "employeeCardId",
);

export const getEmployeeWorkSchedule = makeActionCreator({
  type: ACTIONS.GET_EMPLOYEE_WORK_SCHEDULES,
  payload: cardService.getEmployeeWorkSchedule,
});

export const updateEmployeeWorkSchedule = makeActionCreator(
  {
    type: ACTIONS.UPDATE_EMPLOYEE_WORK_SCHEDULE,
    payload: cardService.updateEmployeeWorkSchedule,
    notification: {
      [NOTIFICATION_VARIANTS.SUCCESS]: "Work schedule successfully updated",
    },
  },
  "cardId",
  "id",
);

export const createEmployeeWorkSchedule = makeActionCreator(
  {
    type: ACTIONS.CREATE_EMPLOYEE_WORK_SCHEDULE,
    payload: cardService.createEmployeeWorkSchedule,
    notification: {
      [NOTIFICATION_VARIANTS.SUCCESS]: "Work schedule successfully created",
    },
  },
  "cardId",
);

export const showEmployee = makeActionCreator(
  {
    type: ACTIONS.SHOW_EMPLOYEE_IN_PLANNER,
    payload: cardService.showEmployee,
    notification: {
      [NOTIFICATION_VARIANTS.SUCCESS]: "Employee successfully updated",
    },
  },
  "cardId",
);

export const hideEmployee = makeActionCreator(
  {
    type: ACTIONS.HIDE_EMPLOYEE_IN_PLANNER,
    payload: cardService.hideEmployee,
    notification: {
      [NOTIFICATION_VARIANTS.SUCCESS]: "Employee successfully updated",
    },
  },
  "id",
);

export const getWorkScheduleDefinitions = makeActionCreator({
  type: ACTIONS.GET_WORK_SCHEDULE_DEFINITIONS,
  payload: cardService.getWorkScheduleDefinitions,
});

export const queryEmployeeWorkSchedule = makeActionCreator({
  type: ACTIONS.QUERY_EMPLOYEE_WORK_SCHEDULE,
  payload: cardService.queryEmployeeWorkSchedule,
});

export const queryEmployeeWorkSchedules = makeActionCreator(
  {
    type: ACTIONS.QUERY_EMPLOYEE_WORK_SCHEDULES,
    payload: cardService.queryEmployeeWorkSchedules,
  },
  "employeeCardId",
);

export const initializeEmployeeForm = employee => dispatch =>
  dispatch(initialize(UPDATE_EMPLOYEE_FORM.ID, employee));

export const employeeCardDetailsOnLoad = employee => dispatch => {
  dispatch(initializeEmployeeForm(employee));

  return Promise.all([
    dispatch(getCrews()),
    dispatch(getMarkers()),
    dispatch(getQualifications()),
    dispatch(getEmployeeGroups()),
  ]);
};

export const updateEmployee = makeActionCreator(
  {
    type: ACTIONS.UPDATE_EMPLOYEE,
    payload: cardService.updateEmployee,
    notification: {
      [NOTIFICATION_VARIANTS.SUCCESS]: "Employee was successfully updated",
      [NOTIFICATION_VARIANTS.INFO]: "Loading...",
    },
  },
  "cardId",
);

export const employeeDetailsFormOnChange = (values, dispatch, props) => {
  const { dirty, employeeCardId } = props;

  if (dirty) {
    return dispatch(updateEmployee(employeeCardId, values.toJS()))
      .then(() => dispatch(getCard(employeeCardId)))
      .then(() => dispatch(initializeEmployeeForm(values))); // make sure the form is dirty if you update the form back to intitial state
  }
};

export const employeeContainerOnLoad = cardId => dispatch => {
  return dispatch(getCard(cardId));
};

export const employeeStayOnLoad = (cardId, groupId) => (dispatch, getState) => {
  const payload = Promise.all([
    dispatch(getCardOpenDeployments(cardId)),
    dispatch(getGroup(groupId)),
    dispatch(getGroupProjects(groupId)),

    dispatch(getCrews()),
    dispatch(getQualifications()),
    dispatch(getReplaceableEmployees(groupId)),
    dispatch(getEmployeesProjects(cardId)),
  ]).then(() => {
    const deploymentDetail = getDeploymentDetailByCardIdFromState(
      getState(),
      cardId,
    );

    if (deploymentDetail) {
      dispatch(
        initialize(CREATE_CARD_MOVE_FORM.ID, {
          groupId,
          [CREATE_CARD_MOVE_FORM.DEPLOYMENT_DETAILS]: {
            ...deploymentDetail
              .delete("deploymentDetailId") // TODO: Ignore this on server
              .toJS(),
            [CREATE_CARD_MOVE_FORM.ARRIVAL]: {
              [CREATE_CARD_MOVE_FORM.DATE]: deploymentDetail.get(
                "deploymentStartDate",
              ),
              [CREATE_CARD_MOVE_FORM.HOUR]: deploymentDetail
                .get("deploymentStartDate")
                .hour(),
              [CREATE_CARD_MOVE_FORM.MINUTE]: deploymentDetail
                .get("deploymentStartDate")
                .minute(),
            },
            [CREATE_CARD_MOVE_FORM.DEPARTURE]: {
              [CREATE_CARD_MOVE_FORM.DATE]: deploymentDetail.get("endDate"),
              [CREATE_CARD_MOVE_FORM.HOUR]: deploymentDetail
                .get("endDate")
                .hour(),
              [CREATE_CARD_MOVE_FORM.MINUTE]: deploymentDetail
                .get("endDate")
                .minute(),
            },
            [CREATE_CARD_MOVE_FORM.FROM]: {
              [CREATE_CARD_MOVE_FORM.DATE]: deploymentDetail.get("startDate"),
              [CREATE_CARD_MOVE_FORM.HOUR]: deploymentDetail
                .get("startDate")
                .hour(),
              [CREATE_CARD_MOVE_FORM.MINUTE]: deploymentDetail
                .get("startDate")
                .minute(),
            },
          },
        }),
      );
    }
  });

  return dispatch({
    type: ACTIONS.EMPLOYEE_STAY_ON_LOAD,
    payload,
    cardId,
    groupId,
  });
};

export const createOrUpdateEmployeeStayOnSubmit = formValues => dispatch => {
  const deploymentDetails = formValues.get("deploymentDetails");
  const cardId = deploymentDetails.get("cardId");

  return dispatch(
    updateCardOpenDeployment(cardId, deploymentDetails),
  ).then(() => dispatch(employeeStayOnLoad(cardId, formValues.get("groupId"))));
};

export const employeeTravelHistoryOnLoad = cardId => dispatch =>
  Promise.all([
    dispatch(getCardAllDeployments(cardId)),
    dispatch(getQualifications()),
  ]);

export const createEmployeeWorkScheduleOnSubmit = toggleModal => dispatch => workSchedule =>
  dispatch(
    createEmployeeWorkSchedule(workSchedule.get("cardId"), workSchedule.toJS()),
  ).then(toggleModal);

export const initializeCreateWorkScheduleForm = (
  cardId,
  startDate = moment(),
  endDate,
) => (dispatch, getState) => {
  const initialWorkScheduleDefinition = getWorkScheduleDefinitionsFromState(
    getState(),
  ).first();

  const initialRotationTimes = 1;

  dispatch(
    initialize(WORK_SCHEDULE_FORM.ID, {
      [WORK_SCHEDULE_FORM.DEFINITION]: initialWorkScheduleDefinition.get("id"),
      [WORK_SCHEDULE_FORM.CARD_ID]: cardId,
      [WORK_SCHEDULE_FORM.START_DATE]: startDate,
      [WORK_SCHEDULE_FORM.END_DATE]:
        endDate ||
        calculateEndDate(
          startDate,
          initialWorkScheduleDefinition,
          initialRotationTimes,
        ),
      [WORK_SCHEDULE_FORM.ROTATIONS]: initialRotationTimes,
    }),
  );

  dispatch(toggleModal({ modalId: CREATE_WORK_SCHEDULE_MODAL_ID }));
};

export const initializeSimpleCreateWorkScheduleForm = (
  cardId,
  startDate,
  endDate,
) => (dispatch, getState) => {
  const initialWorkScheduleDefinition = getWorkScheduleDefinitionsFromState(
    getState(),
  ).find(workScheduleDefinition => workScheduleDefinition.get("dayCount") < 0);

  dispatch(
    initialize(WORK_SCHEDULE_FORM.ID, {
      [WORK_SCHEDULE_FORM.DEFINITION]: initialWorkScheduleDefinition.get("id"),
      [WORK_SCHEDULE_FORM.CARD_ID]: cardId,
      [WORK_SCHEDULE_FORM.START_DATE]: startDate,
      [WORK_SCHEDULE_FORM.END_DATE]: endDate,
    }),
  );

  dispatch(toggleModal({ modalId: SIMPLE_EDIT_WORK_SCHEDULE_MODAL_ID }));
};

const calculateEndDate = (startDate, workScheduleDefinition, rotationTimes) => {
  if (workScheduleDefinition && rotationTimes) {
    const dayCount = workScheduleDefinition.get("dayCount");

    return dayCount >= 0
      ? startDate
          .clone()
          .add(
            workScheduleDefinition.get("dayCount") * rotationTimes - 1,
            "days",
          )
      : startDate;
  }

  return startDate;
};

export const employeeWorkScheduleFormOnChange = (...form) => (
  dispatch,
  getState,
) => {
  const [formValues, , , prevValues] = form; // formValues | dispatch | props | prevValues

  const currEndDate = formValues.get(WORK_SCHEDULE_FORM.END_DATE);
  const prevEndDate = prevValues.get(WORK_SCHEDULE_FORM.END_DATE);
  const currStartDate = formValues.get(WORK_SCHEDULE_FORM.START_DATE);

  if (!currEndDate.isSame(prevEndDate, "day")) {
    return;
  }

  const workScheduleDefinition = getWorkScheduleDefinitionFromState(
    getState(),
    formValues.get(WORK_SCHEDULE_FORM.DEFINITION),
  );

  dispatch(
    change(
      WORK_SCHEDULE_FORM.ID,
      WORK_SCHEDULE_FORM.END_DATE,
      workScheduleDefinition.get("dayCount") > 0
        ? calculateEndDate(
            currStartDate,
            workScheduleDefinition,
            formValues.get(WORK_SCHEDULE_FORM.ROTATIONS),
          )
        : currStartDate,
    ),
  );
};

export const deleteEmployeeWorkSchedule = (
  cardId,
  workScheduleId,
) => dispatch =>
  dispatch({
    cardId,
    workScheduleId,
    type: ACTIONS.DELETE_EMPLOYEE_WORK_SCHEDULE,
    payload: () =>
      cardService.deleteEmployeeWorkSchedule(cardId, workScheduleId),
    confirmationDialog: {
      title: "Delete Work Schedule",
      description: "Are you sure you want to delete this work schedule?",
      confirmButtonText: "Delete",
    },
    notification: {
      [NOTIFICATION_VARIANTS.SUCCESS]: "Work schedule successfully deleted",
    },
  });

export const employeeWorkScheduleOverridesOnUnload = () => dispatch =>
  dispatch({
    type: ACTIONS.UNLOAD_WORK_SCHEDULE_OVERRIDES,
  });

export const getWorkScheduleOverrides = makeActionCreator(
  {
    type: ACTIONS.GET_WORK_SCHEDULE_OVERRIDES,
    payload: cardService.getWorkScheduleOverrides,
  },
  "cardId",
);

export const createWorkScheduleOverride = makeActionCreator(
  {
    type: ACTIONS.CREATE_WORK_SCHEDULE_OVERRIDE,
    payload: cardService.createWorkScheduleOverride,
    notification: {
      [NOTIFICATION_VARIANTS.SUCCESS]:
        "Work schedule override successfully created",
    },
  },
  "cardId",
);

export const updateWorkScheduleOverride = makeActionCreator(
  {
    type: ACTIONS.UPDATE_WORK_SCHEDULE_OVERRIDE,
    payload: cardService.updateWorkScheduleOverride,
    notification: {
      [NOTIFICATION_VARIANTS.SUCCESS]:
        "Work schedule override successfully updated",
    },
  },
  "cardId",
  "workScheduleOverrideId",
);

export const initializeCreateWorkScheduleOverride = cardId => dispatch =>
  dispatch(
    initialize(WORK_SCHEDULE_OVERRIDE_FORM.ID, {
      [WORK_SCHEDULE_OVERRIDE_FORM.CARD_ID]: cardId,
      [WORK_SCHEDULE_OVERRIDE_FORM.REASON]: WORK_SCHEDULE_OVERRIDE_REASONS.SICK,
      [WORK_SCHEDULE_OVERRIDE_FORM.NOTES]: null,
      [WORK_SCHEDULE_OVERRIDE_FORM.START_DATE]: moment(),
      [WORK_SCHEDULE_OVERRIDE_FORM.END_DATE]: moment(),
    }),
  );

export const initializeEditWorkScheduleOverride = (
  cardId,
  workScheduleOverride,
) => dispatch =>
  dispatch(
    initialize(
      WORK_SCHEDULE_OVERRIDE_FORM.ID,
      workScheduleOverride.set(WORK_SCHEDULE_OVERRIDE_FORM.CARD_ID, cardId),
    ),
  );

export const initializeEditWorkScheduleForm = (
  cardId,
  workSchedule,
) => dispatch => {
  dispatch(
    initialize(
      WORK_SCHEDULE_FORM.ID,
      workSchedule.set(WORK_SCHEDULE_FORM.CARD_ID, cardId),
    ),
  );

  dispatch(toggleModal({ modalId: EDIT_WORK_SCHEDULE_MODAL_ID }));
};

export const createWorkScheduleOverrideOnSubmit = toggleModal => dispatch => workScheduleOverride => {
  const cardId = workScheduleOverride.get(WORK_SCHEDULE_OVERRIDE_FORM.CARD_ID);

  return dispatch(
    createWorkScheduleOverride(cardId, workScheduleOverride.toJS()),
  ).then(toggleModal);
};

export const editWorkScheduleOverrideOnSubmit = employeeCardId => dispatch => toggleModal => workScheduleOverride =>
  dispatch(
    updateWorkScheduleOverride(
      employeeCardId,
      workScheduleOverride.get("id"),
      workScheduleOverride.toJS(),
    ),
  ).then(toggleModal);

export const deleteWorkScheduleOverride = (
  cardId,
  workScheduleOverride,
) => dispatch => {
  dispatch({
    cardId,
    type: ACTIONS.DELETE_WORK_SCHEDULE_OVERRIDE,
    workScheduleOverrideId: workScheduleOverride.get("id"),
    notification: {
      [NOTIFICATION_VARIANTS.SUCCESS]:
        "Work schedule override successfully deleted",
    },
    confirmationDialog: {
      confirmButtonText: "Delete",
      title: "Delete Work Schedule Override",
      description: `Are you sure you want to delete this override (${workScheduleOverrideReasonToString(
        workScheduleOverride.get("overrideReason"),
      )})?`,
    },
    payload: () =>
      cardService.deleteWorkScheduleOverride(
        cardId,
        workScheduleOverride.get("id"),
      ),
  });
};

export const showHideEmployee = cardId => (dispatch, getState) => {
  const card = getCardFromState(getState(), cardId).toJS();
  const { employee } = card;

  console.log("caaa", card);
  return employee.hideInPlanning
    ? dispatch(showEmployee(cardId)).then(() =>
        dispatch(employeeContainerOnLoad(cardId)),
      )
    : dispatch(hideEmployee(cardId));
};

export const editEmployeeWorkScheduleOnSubmit = toggleModal => dispatch => workSchedule =>
  dispatch(
    updateEmployeeWorkSchedule(
      workSchedule.get("cardId"),
      workSchedule.get("id"),
      workSchedule.toJS(),
    ),
  ).then(toggleModal);

export const employeeCalendarOnLoad = employeeCardId => dispatch => {
  const payload = Promise.all([
    dispatch(getCardAllDeployments(employeeCardId)),
    dispatch(getWorkScheduleOverrides(employeeCardId)),
    dispatch(
      getCardWorkDays(
        employeeCardId,
        moment().startOf("year"),
        moment().endOf("year"),
      ),
    ),
  ]);

  dispatch({
    payload,
    type: ACTIONS.EMPLOYEE_CALENDAR_ON_LOAD,
  });
};

export const toggleCreateWorkScheduleOverrideModal = (
  cardId,
  startDate,
  endDate,
  callback,
) => dispatch => {
  dispatch(
    initialize(WORK_SCHEDULE_OVERRIDE_FORM.ID, {
      [WORK_SCHEDULE_FORM.CARD_ID]: cardId,
      [WORK_SCHEDULE_FORM.END_DATE]: endDate,
      [WORK_SCHEDULE_FORM.START_DATE]: startDate,
    }),
  );
  dispatch(toggleModal({ modalId: CREATE_WORK_SCHEDULE_OVERRIDE_MODAL_ID }));
  invokeIfFunction(callback);
};

export const toggleEditWorkScheduleOverrideModal = availabilityOverride => dispatch => {
  dispatch(
    initialize(
      WORK_SCHEDULE_OVERRIDE_FORM.ID,
      availabilityOverride.set(WORK_SCHEDULE_OVERRIDE_FORM.CARD_ID),
    ),
  );
  dispatch(toggleModal({ modalId: EDIT_WORK_SCHEDULE_OVERRIDE_MODAL_ID }));
};

export const assignProjectToEmployee = (
  cardId,
  groupId,
  projectId,
) => dispatch => {
  return dispatch(assignEmployee(cardId, projectId)).then(() =>
    dispatch(getEmployeesProjects(cardId)),
  );
};

export const unassignProjectToEmployee = (
  cardId,
  groupId,
  projectId,
) => dispatch => {
  return dispatch(unassignEmployee(cardId, projectId)).then(() =>
    dispatch(getEmployeesProjects(cardId)),
  );
};

export const assignEmployee = makeActionCreator({
  type: ACTIONS.ASSIGN_PROJECT_TO_EMPLOYEE,
  payload: cardService.assignProjectToEmployee,
  notification: {
    [NOTIFICATION_VARIANTS.SUCCESS]: "Employee is assigned to the project",
    [NOTIFICATION_VARIANTS.INFO]: "Loading...",
  },
});

export const unassignEmployee = makeActionCreator({
  type: ACTIONS.UNASSIGN_PROJECT_TO_EMPLOYEE,
  payload: cardService.unassignProjectToEmployee,
  notification: {
    [NOTIFICATION_VARIANTS.SUCCESS]: "Employee is unassigned from the project",
    [NOTIFICATION_VARIANTS.INFO]: "Loading...",
  },
});

export const getEmployeesProjects = makeActionCreator(
  {
    type: ACTIONS.GET_EMPLOYEES_PROJECTS,
    payload: cardService.getEmployeesProjects,
  },
  "cardId",
);
